import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { auth, db, storage } from "./firebase";
import { germanStates, stateList } from "./germanyData";
import { useLanguage } from "./LanguageContext";

function ProviderProfile() {
  const [role, setRole] = useState("");
  const [bio, setBio] = useState("");
  const [qualification, setQualification] = useState("nursing_student");
  const [state, setState] = useState(stateList[0]);
  const [city, setCity] = useState(germanStates[stateList[0]][0]);
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { lang, toggleLang, t } = useLanguage();

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate("/login");
        return;
      }
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) {
        setRole(snap.data().role);
      }
    });
    return () => unsub();
  }, [navigate]);

  const handleStateChange = (newState) => {
    setState(newState);
    setCity(germanStates[newState][0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setUploading(true);

    try {
      const user = auth.currentUser;
      let docUrl = "";

      if (file) {
        const filePath = "verification-docs/" + user.uid + "/" + file.name;
        const fileRef = ref(storage, filePath);
        await uploadBytes(fileRef, file);
        docUrl = await getDownloadURL(fileRef);
      }

      await setDoc(doc(db, "providers", user.uid), {
        uid: user.uid,
        role: role,
        bio: bio,
        qualification: role === "individual" ? qualification : "agency",
        state: state,
        city: city,
        phone: phone,
        website: role === "agency" ? website : "",
        documentUrl: docUrl,
        verificationStatus: "pending",
        createdAt: new Date().toISOString(),
      });

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  if (success) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <button onClick={toggleLang} style={styles.langToggle}>
            {lang === "de" ? "EN" : "DE"}
          </button>
          <h2 style={{ color: "#1a5fb4" }}>
            {lang === "de" ? "Profil eingereicht" : "Profile submitted"}
          </h2>
          <p>
            {lang === "de"
              ? "Ihr Profil wird gepruft. Sie erhalten eine Bestatigung, sobald es freigeschaltet ist."
              : "Your profile is under review. You will be notified once it is approved."}
          </p>
          <button onClick={() => navigate("/dashboard")} style={styles.button}>
            {lang === "de" ? "Zum Dashboard" : "Go to Dashboard"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <button onClick={toggleLang} style={styles.langToggle}>
          {lang === "de" ? "EN" : "DE"}
        </button>
        <button onClick={() => navigate("/dashboard")} style={styles.backLink}>
          {lang === "de" ? "Zuruck" : "Back"}
        </button>
        <h1 style={styles.brand}>{t("appName")}</h1>
        <p style={styles.tagline}>
          {lang === "de" ? "Vervollstandigen Sie Ihr Profil" : "Complete your profile"}
        </p>

        <form onSubmit={handleSubmit} style={{ marginTop: 24 }}>
          <div style={styles.field}>
            <label style={styles.label}>
              {lang === "de" ? "Uber mich / unser Unternehmen" : "About me / our company"}
            </label>
            <textarea value={bio} onChange={(e) => setBio(e.target.value)} required rows={4} style={styles.input} />
          </div>

          {role === "individual" && (
            <div style={styles.field}>
              <label style={styles.label}>{lang === "de" ? "Qualifikation" : "Qualification"}</label>
              <select value={qualification} onChange={(e) => setQualification(e.target.value)} style={styles.input}>
                <option value="nursing_student">{lang === "de" ? "Pflege-/Medizinstudent" : "Nursing/Medical Student"}</option>
                <option value="retired_nurse">{lang === "de" ? "Ehemalige Pflegekraft" : "Retired Nurse"}</option>
                <option value="alltagsbegleiter">{lang === "de" ? "Zertifizierter Alltagsbegleiter" : "Certified Companion"}</option>
                <option value="other">{lang === "de" ? "Sonstige" : "Other"}</option>
              </select>
            </div>
          )}

          <div style={styles.field}>
            <label style={styles.label}>{lang === "de" ? "Telefonnummer" : "Phone Number"}</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} required style={styles.input} />
          </div>

          {role === "agency" && (
            <div style={styles.field}>
              <label style={styles.label}>{lang === "de" ? "Webseite" : "Website"}</label>
              <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" style={styles.input} />
            </div>
          )}

          <div style={styles.field}>
            <label style={styles.label}>{lang === "de" ? "Bundesland" : "State"}</label>
            <select value={state} onChange={(e) => handleStateChange(e.target.value)} style={styles.input}>
              {stateList.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>{lang === "de" ? "Stadt / Bezirk" : "City / District"}</label>
            <select value={city} onChange={(e) => setCity(e.target.value)} style={styles.input}>
              {germanStates[state].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>
              {role === "individual"
                ? (lang === "de" ? "Fuhrungszeugnis / Qualifikationsnachweis" : "Police Clearance / Qualification Certificate")
                : (lang === "de" ? "Gewerbenachweis" : "Business Registration")}
            </label>
            <input type="file" onChange={(e) => setFile(e.target.files[0])} style={styles.input} accept=".pdf,.jpg,.jpeg,.png" />
          </div>

          {error && <p style={styles.error}>{error}</p>}

          <button type="submit" disabled={uploading} style={styles.button}>
            {uploading ? (lang === "de" ? "Wird hochgeladen..." : "Uploading...") : (lang === "de" ? "Profil einreichen" : "Submit Profile")}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #eef5ff 0%, #f7fbff 100%)",
    fontFamily: "'Segoe UI', sans-serif",
    padding: 20,
  },
  card: {
    background: "#ffffff",
    padding: "40px 36px",
    borderRadius: 16,
    boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
    width: "100%",
    maxWidth: 460,
    position: "relative",
  },
  langToggle: {
    position: "absolute",
    top: 16,
    right: 16,
    padding: "4px 10px",
    borderRadius: 6,
    border: "1px solid #ccc",
    background: "#f5f5f5",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    color: "#333",
  },
  brand: { margin: 0, fontSize: 24, color: "#1a5fb4", textAlign: "center" },
  tagline: { textAlign: "center", color: "#555", fontSize: 14, marginTop: 6 },
  field: { marginBottom: 16 },
  label: { display: "block", fontSize: 13, color: "#333", marginBottom: 6, fontWeight: 600 },
  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #ccc",
    fontSize: 14,
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  button: {
    width: "100%",
    padding: "12px",
    background: "#1a5fb4",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 8,
  },
  error: { color: "#d32f2f", fontSize: 13, marginBottom: 8 },
  backLink: {
    background: "none",
    border: "none",
    color: "#1a5fb4",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    marginBottom: 10,
    padding: 0,
  },
};

export default ProviderProfile;

